import { createColumnHelper } from '@tanstack/react-table'
import { formatDate } from '../../../../../../utils/tableFuncs'
import type { FuelReportExpenseDetails } from '../../../../../../types'
import { formatCurrency } from '../../../utils/formatCurrency'
import { useSearchParams } from 'react-router-dom'
import { formatNumber } from '../../../utils/formatNumber'

export default function fuelReportExpenseDetailsColumns() {
  const columnHelper = createColumnHelper<FuelReportExpenseDetails>()
  return [
    columnHelper.accessor('transactionDate', {
      id: 'transactionDate',
      header: () => 'Dato',
      cell: (row) => {
        const value = row.getValue()
        if (!value) return ''

        const dateParts = String(value).split(/[.-]/)
        const normalizedDate =
          dateParts.length === 3 ? `20${dateParts.reverse().join('-')}T00:00:00` : String(value)

        return formatDate(normalizedDate) || ''
      },
    }),
    columnHelper.accessor('transactionTime', {
      id: 'transactionTime',
      header: () => 'Tidspunkt',
      cell: (row) => (row.getValue() ? row.getValue().split(':').slice(0, 2).join(':') : ''),
    }),
    columnHelper.display({
      id: 'regNo',
      header: () => 'Regnr',
      enableSorting: false,
      cell: () => useSearchParams()?.[0]?.get('regNo') || '',
    }),
    columnHelper.accessor('referenceNo', {
      id: 'referenceNo',
      enableSorting: false,
      header: () => 'Kortnr.',
      cell: (row) => (
        <span className="block text-ellipsis overflow-hidden whitespace-nowrap">{row.getValue() || ''}</span>
      ),
    }),
    columnHelper.accessor('retailStationName', {
      id: 'retailStationName',
      enableSorting: false,
      header: () => 'Stasjon',
      cell: (row) => row.getValue() || '',
    }),
    columnHelper.accessor('productDescription', {
      id: 'productDescription',
      enableSorting: false,
      header: () => 'Produkt',
      cell: (row) => row.getValue() || '',
    }),
    columnHelper.accessor('amountIncVat', {
      id: 'amountIncVat',
      meta: {
        mobileFooterLabel: 'Sum beløp inkl. mva',
        alignmentType: 'right',
      },
      header: () => 'Beløp inkl. mva',
      cell: (row) => formatCurrency(row.getValue()) || '',
      footer: ({ table }) => {
        const total = table
          .getFilteredRowModel()
          .rows.reduce((sum, row) => sum + ((row.getValue('amountIncVat') as number) || 0), 0)
        return formatCurrency(total)
      },
    }),
    columnHelper.accessor('volume', {
      id: 'volume',
      sortingFn: 'alphanumeric',
      meta: {
        mobileFooterLabel: 'Totalt mengde',
        alignmentType: 'right',
      },
      header: () => 'Mengde',
      cell: (row) => formatNumber(row.getValue()) || '',
      footer: ({ table }) => {
        const total = table
          .getFilteredRowModel()
          .rows.reduce((sum, row) => sum + ((row.getValue('volume') as number) || 0), 0)
        return formatNumber(total, { digits: { min: 0, max: 0 } })
      },
    }),
    columnHelper.accessor('paidCostPerLiter', {
      id: 'paidCostPerLiter',
      enableSorting: false,
      meta: {
        alignmentType: 'right',
      },
      header: () => 'Pris p/enhet',
      cell: (row) => formatCurrency(row.getValue(), 2) || '',
    }),
    columnHelper.accessor('regKm', {
      id: 'regKm',
      sortingFn: 'alphanumeric',
      meta: {
        alignmentType: 'right',
      },
      header: () => 'Km.stand',
      cell: (row) => formatNumber(row.getValue()) || '',
    }),
  ]
}
